import { KeyValue, type KVRow, Mono, Panel } from '../Panel';
import { Person, type PersonTarget } from '../Person';
import { ClockStatus } from '../TeamClock';
import { bit, dash, money, NotLoaded, pct, teamUnavailable } from '../../lib/display';
import { FEATURE_GROUPS } from '../../lib/features';
import { clockFooter, type TeamClockView } from '../../lib/team-clock';
import type {
  DayHours,
  FacilityDetail,
  FacilityFeatures,
  FacilityTeam,
  StaffMember,
  StaffState,
  SecurityDenialReason,
  SecurityState,
  SecurityUnavailableReason,
} from '../../lib/types';

export const TABS = [
  'General',
  'Responsibilities',
  'Business',
  'Operations',
  'Features',
  'IT',
  'Security',
  'Vendors',
  'Documents',
] as const;

export type TabName = (typeof TABS)[number];

export interface TabProps {
  detail: FacilityDetail;
  onPerson: (t: PersonTarget, anchor: DOMRect) => void;
  /**
   * The PunchWISE clock status for the On-site team, from its own call
   * (`useTeamClock`). Null until it answers, and whenever the panel is not on
   * screen; only the General tab reads it.
   */
  clock?: TeamClockView | null;
}

/**
 * Build a person target with its directory resolution attached.
 *
 * Every people panel goes through this so the Teams actions light up in exactly
 * one place. `detail.directory` is keyed by the display name AS STORED on the
 * facility row, which is the same string being rendered.
 */
function person(
  detail: FacilityDetail,
  role: string,
  name: string | null,
  phone: string | null,
  siteEmail: string | null,
): PersonTarget {
  return {
    role,
    name,
    phone,
    email: null,
    siteEmail,
    dir: name ? detail.directory?.[name.trim()] : undefined,
  };
}

/**
 * A person from `Facility_Staff`. Carries their own email, and their directory
 * resolution comes from `teamDirectory`, keyed by slot and resolved by that
 * email — not from the display-name map the other roles use.
 */
function teamPerson(detail: FacilityDetail, role: string, m: StaffMember): PersonTarget {
  return {
    role,
    name: m.name,
    phone: m.phone,
    email: m.email,
    siteEmail: detail.facility.email,
    dir: detail.teamDirectory?.[m.slot],
    slot: m.slot,
  };
}

/**
 * Every person in one role, in slot order.
 *
 * `'None'` is the DM saying the position does not exist, so those slots are
 * not drawn — the Power App filters them the same way (`Filter(ThisItem.Value,
 * Name <> "None")`). A role left with no one gets ONE card: "None" when the
 * record says so, "Unassigned" when it says nothing, because those are
 * different statements.
 */
function roleTargets(detail: FacilityDetail, role: string, members: StaffMember[]): PersonTarget[] {
  const shown = members.filter((m) => m.name !== 'None');
  if (shown.length) return shown.map((m) => teamPerson(detail, role, m));
  return [
    {
      role,
      name: members.length ? 'None' : null,
      phone: null,
      email: null,
      siteEmail: detail.facility.email,
    },
  ];
}

function teamTargets(detail: FacilityDetail, t: FacilityTeam): PersonTarget[] {
  return [
    teamPerson(detail, t.propertyManagerTitle, t.propertyManager),
    ...roleTargets(detail, 'Assistant Manager', t.assistants),
    ...roleTargets(detail, 'Senior Property Manager', t.seniors),
  ];
}

/** `PropertyManagementType` as it reads in a sentence — "an annex site", not "a Annex Site site". */
const SITE_KIND: Record<string, string> = {
  'Annex Site': 'an annex site',
  Remote: 'a remote site',
  Retail: 'a retail site',
};

/** Where the team on screen came from — its row, and the hub when it is borrowed. */
function TeamSource({ team, propMgmtType }: { team: FacilityTeam; propMgmtType: string | null }) {
  const s = team.source;
  const others = s.otherRowsSetAside;
  return (
    <>
      From Facility Staff
      {s.matchedOn === 'hub' && (
        <>
          {' '}
          — this is {(propMgmtType && SITE_KIND[propMgmtType]) ?? 'a hub-staffed site'}, so its team
          is its hub's, <b>{s.hubName ?? `StoreID ${s.storeId}`}</b>
        </>
      )}
      {s.modified ? ` · updated ${s.modified}${s.modifiedBy ? ` by ${s.modifiedBy}` : ''}` : ''}
      {/* Rows set aside are empty or identical to the one shown — said, not hidden. */}
      {others > 0
        ? ` · ${others} other empty or identical row${others > 1 ? 's' : ''} under StoreID ${s.storeId} set aside`
        : ''}
    </>
  );
}

/** The team could not be shown — and exactly why, in the source's own terms. */
function TeamUnavailable({
  team,
  role,
}: {
  team: Exclude<StaffState, { state: 'present' }>;
  /** The role this stands in for, where the panel holds other people too. */
  role?: string;
}) {
  return (
    <div className="unloaded">
      <b>
        {role ? `${role}: ` : ''}
        {teamUnavailable(team)}
      </b>
      <span>{team.detail}</span>
    </div>
  );
}

/**
 * The clock status a row carries, or undefined for none.
 *
 * Only an `ok` answer, only a row with a `Facility_Staff` slot, and only a
 * slot the API answered. Everything else — no answer yet, `off`,
 * `unavailable`, a placeholder card, someone PunchWISE does not see in use —
 * draws nothing on the row.
 */
function clockFor(t: PersonTarget, clock: TeamClockView | null | undefined) {
  if (clock?.state !== 'ok' || !t.slot) return undefined;
  return clock.members[t.slot];
}

/** Wraps Person so every people panel reports its own anchor rect. */
function People({
  targets,
  onPerson,
  clock,
  facilityFiId = null,
}: {
  targets: PersonTarget[];
  onPerson: TabProps['onPerson'];
  /** The On-site team panel only. */
  clock?: TeamClockView | null;
  facilityFiId?: number | null;
}) {
  return (
    <div className="people">
      {targets.map((t, i) => {
        const member = clockFor(t, clock);
        return (
          <span
            // Four assistants share one role label; the role alone is not a key.
            key={`${t.role}:${i}`}
            onClickCapture={(e) => {
              const el = (e.target as HTMLElement).closest('.person');
              if (el) onPerson(t, el.getBoundingClientRect());
            }}
          >
            <Person
              target={t}
              onOpen={() => undefined}
              aside={member ? <ClockStatus member={member} facilityFiId={facilityFiId} /> : undefined}
            />
          </span>
        );
      })}
    </div>
  );
}

/**
 * Three outcomes, not two.
 *
 * A time pair renders as hours. An explicit `closed` flag renders as Closed.
 * Everything else — no times AND no flag — renders as "Not recorded", because
 * it is not the same claim. Collapsing the third case into "Closed" is what
 * made all 376 facilities look shut seven days a week for twelve days: the 42
 * hour columns were being read under names the source does not use, so every
 * value came back null and null was being printed as a confident "Closed".
 *
 * One definition, one place to fix. The Security tab's gate-access rows used to
 * keep their own copy of the two-state shortcut, which is exactly how a fix to
 * this function misses half the screens that need it. Those rows are gone with
 * the duplicated gate-hours card — access hours are on General, all seven days
 * — so this is now the only place a DayHours is turned into words. Keep it that
 * way.
 */
function fmtHours(d: DayHours | undefined) {
  if (d?.open && d?.close) return `${d.open} – ${d.close}`;
  if (d?.closed === true) return <span className="closed">Closed</span>;
  // Half a pair is half an answer. Returning the bare time would read as a
  // complete one, which is the same conflation one branch up.
  if (d?.open) return `Opens ${d.open}`;
  if (d?.close) return `Closes ${d.close}`;
  return <span className="na">Not recorded</span>;
}

function HoursTable({ office, access }: { office: DayHours[]; access: DayHours[] }) {
  // JS getDay() is Sunday-first; the table is Monday-first.
  const todayIdx = (new Date().getDay() + 6) % 7;


  return (
    <table className="hrs">
      <thead>
        <tr>
          <th />
          <th>Office hours</th>
          <th>Access hours</th>
        </tr>
      </thead>
      <tbody>
        {office.map((o, i) => (
          <tr key={o.day} className={todayIdx === i ? 'today' : undefined}>
            <td className="day">{o.day}</td>
            <td>{fmtHours(o)}</td>
            <td>{fmtHours(access[i])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const isExpired = (iso: string | null): boolean => !!iso && new Date(iso) < new Date();

function CoiCell({ ins }: { ins: string | null }) {
  if (!ins) return <span className="na">—</span>;
  return isExpired(ins) ? (
    <span className="warn-t">{ins} · expired</span>
  ) : (
    <span className="mono">{ins}</span>
  );
}

/**
 * One feature resolved from BOTH of its source columns.
 *
 * Speaks the same three-state vocabulary as `bit()` (`cls`/`k`/`t`), so a row
 * can be rendered from a FeatureFact exactly as it used to be rendered from a
 * Bit — the difference is what went into deciding it.
 */
interface FeatureFact {
  cls: 'yes' | 'no' | 'unk';
  k: string;
  /** Plain-language state, for screen readers and for KeyValue rows. */
  t: string;
  /** The recorded count, when one exists. 0 is a count; null is no count. */
  qty: number | null;
  /** Short label for a source disagreement, or null when the columns agree. */
  conflict: string | null;
}

/**
 * Resolve a feature from its flag column AND its quantity column TOGETHER.
 *
 * WHY THIS EXISTS: every renderer here used to consult the flag alone and read
 * the quantity only to print a decorative badge. On ALAORI that made the
 * Equipment panel say "0 of 4" while the Dollies row beside it showed an empty
 * circle and a badge reading 1 — `Dolly` is false, `Dolly_Quantity` is 1, and
 * the Power App this module replaces reads the same row and says the site has
 * one dolly. It is right: a recorded count above zero is a POSITIVE
 * observation, while a false flag is only the absence of one, so presence is
 * `flag === true OR quantity > 0`.
 *
 * WHERE THE TWO COLUMNS CONTRADICT EACH OTHER, SAY SO. This is a system of
 * record; the disagreement is a fact about the source and belongs on the
 * screen. Picking a winner silently would make this view a worse witness than
 * the table behind it — and a bare circle beside a number, which is what
 * shipped, is the worst of both.
 */
function featureFact(
  flagRaw: boolean | number | string | null | undefined,
  qtyRaw: boolean | number | string | null | undefined,
): FeatureFact {
  const b = bit(flagRaw);

  // Only a NUMBER is a count. `readFeatures` decides flag-vs-count by the
  // column's declared SQL type, and a quantity column that is declared bit
  // really does arrive as `true` (the source has such a row — see the
  // Apartment_Quantity note in api/src/services/facility-repository.ts). "true"
  // is not a quantity, so it is not treated as one. Where a feature has no
  // quantity column at all, the flag's own magnitude IS the count — that is
  // Moving_Truck, an int with no Moving_Truck_Quantity beside it.
  const qty = typeof qtyRaw === 'number' ? qtyRaw : b.n;

  const counted = qty !== null && qty > 0;
  const flagYes = b.cls === 'yes';
  const flagAnswered = b.cls !== 'unk';

  // Neither column was answered. Stays unanswered — never "No" and never
  // "None", which would be inventing an answer nobody gave.
  if (!flagAnswered && qty === null) {
    return { cls: 'unk', k: '·', t: 'Not answered', qty: null, conflict: null };
  }

  const disagrees = flagAnswered && qty !== null && flagYes !== counted;

  if (flagYes || counted) {
    let t = 'Yes';
    if (disagrees && !flagYes) t = `Yes — ${qty} recorded, flag says no`;
    else if (disagrees) t = 'Yes — flagged, but 0 recorded';
    else if (counted) t = `Yes — ${qty}`;
    return {
      cls: 'yes',
      k: '✓',
      t,
      qty,
      conflict: disagrees ? (flagYes ? 'count says 0' : 'flag says no') : null,
    };
  }

  // Both columns agree there is nothing here, or the only answered one says so.
  return { cls: 'no', k: '○', t: 'No', qty, conflict: null };
}

// ─── General ────────────────────────────────────────────────────────────────

/**
 * Render a "has it / how many" pair from the raw feature bag.
 *
 * The flag and the quantity are separate columns and disagree in real data: a
 * site can be flagged as having apartments with no count recorded, and a count
 * can exist where nobody set the flag. An unanswered pair stays unanswered
 * rather than becoming "None", which would be an invented fact.
 *
 * WHAT CHANGED: this opened with `if (flag === false) return 'None'`, so a
 * false flag short-circuited before the count was ever read and a site whose
 * flag says no and whose quantity says 1 reported "None" — the same defect the
 * Features grid had, in a second place. Both columns now go through
 * featureFact, so this row, the Features row and the Features panel header all
 * answer the question the same way, and a contradiction between the columns is
 * shown rather than resolved in silence.
 */
function countOf(
  feat: FacilityFeatures | null,
  flagCol: string | null,
  countCol: string,
): React.ReactNode {
  if (!feat) return null;
  const f = featureFact(flagCol ? feat[flagCol] : null, feat[countCol]);
  if (f.cls === 'unk') return null;
  if (f.cls === 'no') return 'None';
  if (f.conflict) return <span className="warn-t">{f.t}</span>;
  // A row with no flag column at all (Apartment bedrooms) is a pure quantity:
  // the count is the whole answer, so "Yes — 3" would only add noise.
  if (flagCol === null) return f.qty !== null ? String(f.qty) : 'Yes';
  return f.qty !== null && f.qty > 0 ? `Yes — ${f.qty}` : 'Yes';
}

export function GeneralTab({ detail, onPerson, clock }: TabProps) {
  const { facility: x, ops, hours, features } = detail;
  const units = ops.present ? ops.data.units : null;
  const feat = features.present ? features.data : null;
  const team = x.team.state === 'present' ? teamTargets(detail, x.team.data) : [];
  // The footer credits PunchWISE only for what is actually on the panel.
  const clockLine = clockFooter(clock, team.filter((t) => clockFor(t, clock)).length);

  return (
    <div className="cols">
      <div className="col">
        <Panel title="Site">
          <KeyValue
            rows={[
              ['Brand', x.brand],
              ['Management type', x.mgmt],
              ['Category', x.category],
              [
                'Address',
                x.street || x.city ? (
                  <>
                    {x.street}
                    <br />
                    {[x.city, x.state].filter(Boolean).join(', ')} {x.zip ?? ''}
                  </>
                ) : null,
              ],
              ['Phone', x.phone],
              ['Email', x.email ? <a href={`mailto:${x.email}`}>{x.email}</a> : null],
              [
                'Coordinates',
                x.lat !== null && x.lng !== null ? (
                  <Mono>
                    {x.lat.toFixed(6)}, {x.lng.toFixed(6)}
                  </Mono>
                ) : null,
              ],
            ]}
          />
        </Panel>

        <Panel title="Identifiers">
          <KeyValue
            rows={[
              ['Yardi code', <Mono key="y">{x.yardiCode}</Mono>],
              ['Store ID', x.storeId !== null ? <Mono key="s">{x.storeId}</Mono> : null],
              ['SiteLink code', x.siteLinkCode ? <Mono key="l">{x.siteLinkCode}</Mono> : null],
              [
                'SiteLink ID',
                // `0` is a real value meaning not provisioned. Showing a bare
                // zero here reads as an id someone might go looking for.
                x.siteLinkId && x.siteLinkId !== '0' ? (
                  <Mono key="li">{x.siteLinkId}</Mono>
                ) : (
                  <span className="warn-t">Not provisioned</span>
                ),
              ],
              ['Payroll dept code', x.payroll ? <Mono key="p">{x.payroll}</Mono> : null],
              ['Unit quantity', units !== null ? <Mono key="u">{units}</Mono> : null],
            ]}
          />
        </Panel>

        {/* Apartments and commercial units are on Facility_Features, which this
            module already reads in full — they simply had no rendering path.
            The Power App shows both on its General screen, so they belong here
            rather than buried among sixty feature bits. */}
        <Panel title="Mixed use">
          <KeyValue
            rows={[
              ['Apartments', countOf(feat, 'Apartments', 'Apartment_Quantity')],
              ['Apartment bedrooms', countOf(feat, null, 'Apartment_Bedroom_Quantity')],
              ['Commercial units', countOf(feat, 'Commercial_Units', 'Commercial_Unit_Quantity')],
            ]}
          />
        </Panel>
      </div>

      <div className="col">
        <Panel title="Hours">
          {hours.present ? (
            <HoursTable office={hours.data.office} access={hours.data.access} />
          ) : (
            <NotLoaded what="business hours" />
          )}
        </Panel>

        <Panel
          title="On-site team"
          footer={
            x.team.state === 'present' ? (
              <>
                <TeamSource team={x.team.data} propMgmtType={x.propMgmtType} />
                {clockLine && (
                  <div
                    className="clk-src"
                    title={clock?.state === 'unavailable' ? clock.detail : undefined}
                  >
                    {clockLine}
                  </div>
                )}
              </>
            ) : undefined
          }
        >
          {x.team.state === 'present' ? (
            <People onPerson={onPerson} targets={team} clock={clock} facilityFiId={x.fiId} />
          ) : (
            <TeamUnavailable team={x.team} />
          )}
        </Panel>
      </div>
    </div>
  );
}

// ─── Responsibilities ───────────────────────────────────────────────────────

export function ResponsibilitiesTab({ detail, onPerson }: TabProps) {
  const x = detail.facility;
  return (
    <div className="cols">
      <div className="col">
        <Panel title="Field leadership">
          <People
            onPerson={onPerson}
            targets={[
              person(detail, 'District Manager', x.district, null, x.email),
              person(detail, 'Regional Manager', x.rm, null, null),
              // From Facility_Staff, like the On-site team — both seniors.
              ...(x.team.state === 'present'
                ? roleTargets(detail, 'Senior Property Manager', x.team.data.seniors)
                : []),
            ]}
          />
          {/* No team, no senior card — but the role is not silently dropped. */}
          {x.team.state !== 'present' && (
            <TeamUnavailable team={x.team} role="Senior Property Manager" />
          )}
        </Panel>
        <Panel title="Property services">
          <People
            onPerson={onPerson}
            targets={[
              person(detail, 'Property Services Coordinator', x.psc, null, null),
              person(detail, 'Maintenance Tech', x.tech, null, null),
              person(detail, 'Construction Project Manager', x.cpm, null, null),
            ]}
          />
        </Panel>
      </div>

      <div className="col">
        <Panel title="Corporate">
          <People
            onPerson={onPerson}
            targets={[
              person(detail, 'Accounting Manager', x.acct, null, null),
              person(detail, 'Human Resources', x.hr, null, null),
              person(detail, 'Client Relations Manager', x.crm, null, null),
              // Both columns exist on Facility_Information and were never read.
              person(detail, 'Asset Management Coordinator', x.amc, null, null),
              person(detail, 'Licences / Energy Compliance', x.lec, null, null),
            ]}
          />
        </Panel>

        {/* Derived, not stored: the escalation path names the real people and
            says who to go to instead where a role is empty. */}
        <Panel title="Escalation path">
          <ol className="esc">
            <li>
              <b>Property Manager</b>
              <span>
                {x.team.state !== 'present'
                  ? `${teamUnavailable(x.team)} — escalate to DM`
                  : x.pm === 'Now Hiring'
                    ? 'Vacant — escalate to DM'
                    : (x.pm ?? 'Unassigned — escalate to DM')}
              </span>
            </li>
            <li>
              <b>District Manager</b>
              <span>{x.district ?? 'Unassigned — escalate to RM'}</span>
            </li>
            <li>
              <b>Regional Manager</b>
              <span>{x.rm ?? 'Unassigned'}</span>
            </li>
            <li>
              <b>Client Relations</b>
              <span>Owner-facing — {x.crm ?? 'unassigned'}</span>
            </li>
          </ol>
        </Panel>
      </div>
    </div>
  );
}

// ─── Business ───────────────────────────────────────────────────────────────

export function BusinessTab({ detail }: TabProps) {
  const { facility: x, ops, owners, tracking } = detail;
  const o = ops.present ? ops.data : null;

  return (
    <div className="cols">
      <div className="col">
        <Panel title="Legal entity">
          <KeyValue
            rows={[
              ['Entity legal name', x.entity],
              ['FEIN', x.fein ? <Mono key="f">{x.fein}</Mono> : null],
              ['W-9 line 1', o?.w9a ?? null],
              ['W-9 line 2', o?.w9b ?? null],
              ['Portfolio group', x.portfolio],
              ['Region', x.region],
            ]}
          />
        </Panel>
        <Panel title="Management">
          <KeyValue
            rows={[
              ['Management type', x.mgmt],
              ['Property management type', x.propMgmtType],
              ['Previous management type', x.prevMgmt],
              ['Conversion date', x.converted],
              ['Sold / management end date', x.soldEnd],
              ['Rebrand date', x.rebrand],
              ['Office remodel date', x.officeRemodel],
              ['Payroll dept code', x.payroll ? <Mono key="p">{x.payroll}</Mono> : null],
              ['Dept code division', x.deptDivision],
              ['Billing cycle', o?.billing ?? null],
            ]}
          />
        </Panel>
      </div>

      <div className="col">
        <Panel title="Tax and jurisdiction">
          {o ? (
            <KeyValue
              rows={[
                ['Sales tax', pct(o.salesTax)],
                ['Rent tax', pct(o.rentTax)],
                ['Time zone', o.tz],
              ]}
            />
          ) : (
            <NotLoaded what="operations" />
          )}
        </Panel>

        <Panel title="Tenant protection">
          {o ? (
            <KeyValue
              rows={[
                ['Program', o.altIns],
                ['Coverage — min', money(o.minCov)],
                ['Coverage — max', money(o.maxCov)],
                ['Premium — min', o.minPrem !== null ? `$${o.minPrem.toFixed(2)}` : null],
                ['Premium — max', o.maxPrem !== null ? `$${o.maxPrem.toFixed(2)}` : null],
              ]}
            />
          ) : (
            <NotLoaded what="operations" />
          )}
        </Panel>

        {/* `Facility_Owners` is a JOIN table, so a facility can have more than
            one owner. Rendered as a list rather than as three fields. */}
        <Panel title="Ownership">
          {owners.present && owners.data.length ? (
            <div className="ownr">
              {owners.data.map((ow, i) => (
                <div className="ownr-r" key={`${ow.name}-${ow.email}-${i}`}>
                  <b>{ow.name ?? <span className="na">Unnamed owner</span>}</b>
                  <span>
                    {ow.email ? <a href={`mailto:${ow.email}`}>{ow.email}</a> : null}
                    {ow.email && ow.phone ? ' · ' : null}
                    {ow.phone ? (
                      <a href={`tel:${ow.phone.replace(/[^\d+]/g, '')}`}>{ow.phone}</a>
                    ) : null}
                    {!ow.email && !ow.phone ? <span className="na">No contact recorded</span> : null}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="unloaded">
              <b>No owner on record</b>
              <span>This facility has no row in the owner list.</span>
            </div>
          )}
        </Panel>

        {/* CallPotential routes a DIFFERENT number per advertising source, so
            this is a list, not the single "IVR Tracking Number" field the
            Power App shows. The table has no StoreID — it is matched on the
            SiteLink code, which two facilities can legitimately share when an
            annex sits on its parent's SiteLink site. Where that happens the
            panel says so rather than implying the number is exclusive. */}
        <Panel
          title="Call tracking"
          footer={
            tracking.present && tracking.data.sharedWith > 0 ? (
              <>
                Matched on SiteLink code {x.siteLinkCode}, which {tracking.data.sharedWith} other{' '}
                {tracking.data.sharedWith === 1 ? 'facility shares' : 'facilities share'} — they are
                one SiteLink site, so these numbers serve all of them.
              </>
            ) : undefined
          }
        >
          {tracking.present ? (
            <div className="tbl-x">
              <table className="mini-t">
                <thead>
                  <tr>
                    <th>Number</th>
                    <th>Advertising source</th>
                  </tr>
                </thead>
                <tbody>
                  {tracking.data.numbers.map((t, i) => (
                    <tr key={`${t.number}-${t.adId}-${i}`}>
                      <td className="mono">
                        <a href={`tel:${(t.number ?? '').replace(/[^\d+]/g, '')}`}>{t.number}</a>
                      </td>
                      <td>{t.adName ?? <span className="na">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="unloaded">
              <b>No tracking numbers</b>
              <span>
                {x.siteLinkCode
                  ? `No CallPotential rows carry SiteLink code ${x.siteLinkCode}.`
                  : 'This facility has no real SiteLink code, so tracking numbers cannot be matched to it.'}
              </span>
            </div>
          )}
        </Panel>

        {/* Cross-module links are intentionally inert until each target module
            exposes a deep link for a facility. They read as labels, not as
            buttons that silently do nothing. */}
        <Panel title="Linked WISE records">
          <div className="links">
            <span style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 15px', fontSize: 12.5, borderBottom: '1px solid var(--border)' }}>
              <span className="lm">TaxWISE</span>
              Parcel and assessment records
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 15px', fontSize: 12.5, borderBottom: '1px solid var(--border)' }}>
              <span className="lm">CounselWISE</span>
              Matters naming {x.entity ?? 'this entity'}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 15px', fontSize: 12.5 }}>
              <span className="lm">ContractWISE</span>
              Contracts covering this site
            </span>
          </div>
          <div className="pnl-foot">
            Deep links open once each module publishes a per-facility route.
          </div>
        </Panel>
      </div>
    </div>
  );
}

// ─── Operations ─────────────────────────────────────────────────────────────

/**
 * A yes / no / not-answered pill.
 *
 * The class used to be `b.cls === 'yes' ? 'yes' : 'no'`, which threw the
 * three-state contract away at the very last step: a program nobody recorded
 * rendered as the same grey pill as an explicit No, asserting a fact the source
 * does not carry. The text already said "Not answered" — only the styling
 * lied, which is the harder kind to notice. `.feat.unk` and `.sk.unk` get this
 * right elsewhere; this was the one place that dropped it.
 *
 * The `na` rider is deliberate: facilitywise.css defines `.fl-v.yes` and
 * `.fl-v.no` but has no `.fl-v.unk`, and that file is extracted verbatim from
 * the prototype and re-extracted rather than hand-patched (see CLAUDE.md). So
 * the unanswered pill borrows `.na`, the same quiet grey the em-dash uses for
 * "nothing recorded", instead of rendering as an unstyled pill. When the
 * prototype grows a real `.fl-v.unk` rule, drop the rider — `b.cls` is already
 * on the element and will pick it up. `.feat.unk` on the Features grid is the
 * remaining renderer that gets three states right by itself; the `.sk.unk`
 * rows that used to be its other example went with the Security tab's copy of
 * the feature checkboxes.
 */
function FlagRow({ label, value }: { label: string; value: boolean | null }) {
  const b = bit(value);
  return (
    <div className="flagrow">
      <span className="fl-l">{label}</span>
      <span className={`fl-v ${b.cls}${b.cls === 'unk' ? ' na' : ''}`}>{b.t}</span>
    </div>
  );
}

export function OperationsTab({ detail }: TabProps) {
  const { facility: x, ops } = detail;
  if (!ops.present) return <NotLoaded what="operations" />;
  const o = ops.data;

  return (
    <div className="cols">
      <div className="col">
        <Panel title="Operating profile">
          <KeyValue
            rows={[
              ['Billing cycle', o.billing],
              ['Unit quantity', o.units !== null ? <Mono key="u">{o.units}</Mono> : null],
              ['Time zone', o.tz],
              // manned is tri-state: null means nobody recorded it, which is
              // not the same as an unmanned site.
              ['Staffing', x.manned === null ? null : x.manned ? 'Manned' : 'Unmanned'],
              ['Legal name on file', o.legal],
            ]}
          />
        </Panel>
        <Panel title="Program participation">
          <div className="flags">
            <FlagRow label="Charity Storage" value={o.charity} />
            <FlagRow label="KureIT managed" value={o.kureIT} />
            <FlagRow label="Lockdown enabled" value={o.lockdown} />
          </div>
        </Panel>
      </div>

      <div className="col">
        <Panel title="External system IDs">
          <KeyValue
            rows={[
              ['CallPotential location', o.cpLocation ? <Mono key="c">{o.cpLocation}</Mono> : null],
              ['StorageTreasures', o.stId ? <Mono key="s">{o.stId}</Mono> : null],
              ['Heitman', o.heitmanId ? <Mono key="h">{o.heitmanId}</Mono> : null],
              ['SiteLink LCode', x.siteLinkCode ? <Mono key="lc">{x.siteLinkCode}</Mono> : null],
            ]}
          />
        </Panel>
        <Panel title="Insurance program">
          <KeyValue
            rows={[
              ['Alternative to insurance', o.altIns],
              [
                'Coverage range',
                o.minCov !== null && o.maxCov !== null
                  ? `${money(o.minCov)} – ${money(o.maxCov)}`
                  : null,
              ],
              [
                'Premium range',
                o.minPrem !== null && o.maxPrem !== null
                  ? `$${o.minPrem.toFixed(2)} – $${o.maxPrem.toFixed(2)}`
                  : null,
              ],
            ]}
          />
        </Panel>
      </div>
    </div>
  );
}

// ─── Features ───────────────────────────────────────────────────────────────

export function FeaturesTab({ detail }: TabProps) {
  if (!detail.features.present) return <NotLoaded what="features" />;
  const F = detail.features.data;

  return (
    <div className="feat-grid">
      {FEATURE_GROUPS.map((g) => {
        // Answered and yes are counted separately, because "3 of 9" over nine
        // questions where two were never answered is a different fact from
        // "3 of 7". The denominator stays answered-only on purpose.
        //
        // The header and the rows are now resolved from ONE list of facts. They
        // used to be computed from different expressions over the flag column
        // — which is how the panel could say "0 of 4" directly above a row
        // showing a quantity of 1. Anything that changes a row's state has to
        // change the header with it, so there is only one place to change.
        const rows = g.items.map((item) => {
          const qtyCol = item[2];
          return { item, f: featureFact(F[item[1]], qtyCol ? F[qtyCol] : null) };
        });
        const answered = rows.filter((r) => r.f.cls !== 'unk');
        const yes = answered.filter((r) => r.f.cls === 'yes');
        const unanswered = rows.length - answered.length;

        return (
          <section className="pnl" key={g.g}>
            <div className="pnl-h">
              <h3>{g.g}</h3>
              {/* "of 4" counts the ANSWERED items, not the four-of-five rows
                  on screen. The arithmetic is deliberate; the label was what
                  hid it, so the label now names its universe. */}
              <span className="pnl-n">
                {yes.length} of {answered.length} answered
                {unanswered > 0 ? ` · ${unanswered} unanswered` : ''}
              </span>
            </div>
            <div className="feats">
              {rows.map(({ item: i, f }) => (
                <div className={`feat ${f.cls}`} key={i[1]}>
                  <span className="fk" aria-hidden="true">
                    {f.k}
                  </span>
                  <span className="fn">{i[0]}</span>
                  {/* f.t carries the count and any contradiction, so a screen
                      reader hears "Dollies, yes, 1 recorded, flag says no"
                      rather than "Dollies, no, 1". Everything visible in this
                      row is therefore aria-hidden — the tick, the conflict chip
                      and the badge are all SHORTHAND for what f.t already says
                      in full, and leaving any of them exposed makes the row
                      read twice ("…flag says no, flag says no"). */}
                  <span className="fw-sr">{f.t}</span>
                  {f.conflict ? (
                    <span className="warn-t" aria-hidden="true">
                      {f.conflict}
                    </span>
                  ) : null}
                  {/* Tested for presence, not truthiness: a recorded 0 is an
                      answered "none" and prints its badge. `qty ? …` hid it,
                      making it indistinguishable from never answered — which
                      undoes the one distinction the API works hardest to keep.
                      f.qty is a number or nothing, so a bit-typed quantity can
                      no longer render the word "true". */}
                  {f.qty !== null ? (
                    <span className="fq mono" aria-hidden="true">
                      {f.qty}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ─── IT ─────────────────────────────────────────────────────────────────────

export function ItTab({ detail }: TabProps) {
  const { facility: x, ops, vendors } = detail;
  const o = ops.present ? ops.data : null;
  // The answering service comes from the vendor record — not invented.
  const answering = vendors.present ? vendors.data.find((v) => v.s === 'After Hours') : undefined;

  return (
    <div className="cols">
      <div className="col">
        <Panel title="Identifiers">
          <KeyValue
            rows={[
              ['Yardi code', <Mono key="y">{x.yardiCode}</Mono>],
              ['Store ID', x.storeId !== null ? <Mono key="s">{x.storeId}</Mono> : null],
              ['SiteLink code', x.siteLinkCode ? <Mono key="l">{x.siteLinkCode}</Mono> : null],
              [
                'SiteLink ID',
                x.siteLinkId && x.siteLinkId !== '0' ? (
                  <Mono key="li">{x.siteLinkId}</Mono>
                ) : (
                  <span className="warn-t">Not provisioned</span>
                ),
              ],
              ['Payroll dept code', x.payroll ? <Mono key="p">{x.payroll}</Mono> : null],
            ]}
          />
        </Panel>
        <Panel title="Connected systems">
          {o ? (
            <KeyValue
              rows={[
                ['CallPotential location ID', o.cpLocation ? <Mono key="c">{o.cpLocation}</Mono> : null],
                ['StorageTreasures ID', o.stId ? <Mono key="s">{o.stId}</Mono> : null],
                ['KureIT managed', bit(o.kureIT).t],
                ['Lockdown enabled', bit(o.lockdown).t],
              ]}
            />
          ) : (
            <NotLoaded what="operations" />
          )}
        </Panel>
      </div>

      <div className="col">
        <Panel title="Answering service">
          {vendors.present ? (
            <KeyValue
              rows={[
                ['After-hours vendor', answering?.v ?? null],
                ['Contact', answering?.c ?? null],
                ['Number', answering?.ph ?? null],
              ]}
            />
          ) : (
            <NotLoaded what="vendor" />
          )}
        </Panel>
        <Panel title="Access and accounts">
          <div className="links">
            <span style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 15px', fontSize: 12.5, borderBottom: '1px solid var(--border)' }}>
              <span className="lm">WISE-IAM</span>
              Site staff roster and entitlements
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 15px', fontSize: 12.5 }}>
              <span className="lm">OMS·ODS</span>
              Open operations tickets for this site
            </span>
          </div>
          <div className="pnl-foot">
            Deep links open once each module publishes a per-facility route.
          </div>
        </Panel>
      </div>
    </div>
  );
}

// ─── Security ───────────────────────────────────────────────────────────────

/**
 * WHY THIS TAB WAS REBUILT.
 *
 * It used to carry three cards and not one of them held anything of its own:
 * eleven feature bits that are ALL on the Features tab, a regex-filtered subset
 * of the Vendors tab with three of its six columns, and two of the seven
 * access-hours rows the General tab already prints in full. The owner's words:
 * "Security and Features should no way display the same thing."
 *
 * The security data people actually open this tab for was never in the module
 * at all. It lives in six SharePoint lists on the FacilityDataCenter site, and
 * the SQL mirror this module reads replicates 8 of the 25 lists the Power App
 * uses — these six are among the 17 it skips. That is why no amount of looking
 * in SharePoint.dbo ever found a keybox code: it is not there to find. The API
 * reads the lists through Graph and this tab renders them, and nothing that
 * another tab already owns in full.
 *
 * ⚠️ THE SIXTH LIST IS A PARTIAL READ. `Facility IT Information` has 61
 * authored columns and the API requests the TEN in the legacy app's "Gate
 * Access Control" block, because that block is the only part of the list the
 * old Security tab shows. Rule 4, below, is why the boundary is drawn at the
 * READ and not here: the portal cannot un-send a column.
 *
 * EVERYTHING ON THIS SCREEN IS A PHYSICAL SECURITY CREDENTIAL. A gate code lets
 * a person off the property with someone else's belongings; the key box and the
 * safe combination are physical access; the DVR rows are live logins. Three
 * rules follow, and none of them is stylistic:
 *
 *  1. A value reaches the DOM as TEXT and appears nowhere else — never in a
 *     `title`, a `data-` attribute, an aria-label, a tooltip, a React key or a
 *     console statement. Each of those is a second copy in a place nobody
 *     checks when they ask who can see this.
 *  2. Withheld means ABSENT. The API does not send a sensitive value to a
 *     caller without the permission, so there is no masking, blanking or
 *     CSS-hiding here to get wrong: a column is rendered only when the column
 *     arrived. Never write a branch that expects a withheld field to be
 *     present-but-empty — in this payload it does not exist.
 *  3. Never print "none on file" for a list we did not manage to read. The
 *     not-connected state is a separate, louder rendering, because this module
 *     has already spent twelve days telling people a source was empty when it
 *     was being read under column names the source does not use.
 *  4. EVERY FIELD THE API SENDS IS RENDERED HERE. A column the reader selects,
 *     maps, types and serialises but that no panel prints is still in the JSON,
 *     one devtools panel away — which is the `sv.notes` defect this module has
 *     already shipped once. So when a field is added to `SECURITY_LISTS` on the
 *     API side, it gets a row or a column on this screen in the same change, or
 *     it comes back out of the catalogue. Nothing on either side of the hand
 *     mirror will tell you if it does not.
 */

/**
 * A security list that WAS read and holds nothing for this facility.
 *
 * Deliberately distinct from the `not_loaded` states below: this sentence is a
 * claim about the PROPERTY, and the API only lets it be printed once the list
 * has actually answered — `absent` is a different state from `not_loaded` for
 * exactly this reason. `.unloaded` styles ANY descendant span as a block, so
 * the body here is plain text — no nested `Mono`.
 */
function NoSecurityRows({
  list,
  code,
  storeId,
}: {
  list: string;
  code: string;
  storeId: number | null;
}) {
  return (
    <div className="unloaded">
      <b>No {list} on file</b>
      <span>
        {`The list was read and returned no row for this facility on either key these lists are ` +
          `joined by: the Yardi code ${code}` +
          (storeId === null
            ? ', and it carries no StoreID to fall back on.'
            : `, or StoreID ${storeId}.`)}
      </span>
    </div>
  );
}

/**
 * Headline for each way a section can fail to load.
 *
 * Every one of these is a statement about US, never about the property. They
 * are worded apart on purpose: "not connected" is an administrative action
 * somebody can take, "could not be read" is a fault, and "duplicate rows" is a
 * data problem in SharePoint that this module refuses to guess its way past.
 *
 * ⚠️ `forbidden` USED TO READ "Not connected yet" and no longer may. The
 * per-site Graph grant on FacilityDataCenter was applied on 2026-09-18 and the
 * reads work, so a 403 today means the grant was taken away — which is a fault
 * to report now, not a switch somebody has yet to flip. "Yet" would send the
 * reader to wait for a thing that already happened.
 */
const UNAVAILABLE_TITLE: Record<SecurityUnavailableReason, string> = {
  not_configured: 'Not connected',
  forbidden: 'Access refused',
  list_not_found: 'Not found on the SharePoint site',
  unreachable: 'Could not be reached',
  read_failed: 'Could not be read',
  no_join_key: 'Cannot be matched to this facility',
  ambiguous_source: 'Duplicate rows in the source',
};

/**
 * A section the API could not answer for, with the reason it gave.
 *
 * ⚠️ NEVER "no codes on file". A 403, an unreachable tenant and a list nobody
 * has granted us are all statements about FacilityWISE; "this site has no gate
 * codes" is a statement about the site, and the person reading this screen is
 * about to act on the difference. The module has already spent twelve days
 * telling people a source was empty when it was being read under column names
 * the source does not use.
 *
 * `detail` comes from the API and carries list names, counts and classifiers
 * only — never a value.
 */
function SecurityUnavailable({
  what,
  reason,
  detail,
  code,
}: {
  what: string;
  reason: SecurityUnavailableReason;
  detail: string | null;
  code: string;
}) {
  return (
    <div className="unloaded">
      <b>
        {what} — {UNAVAILABLE_TITLE[reason] ?? 'Could not be read'}
      </b>
      <span>
        {detail ? `${detail} ` : ''}
        {reason === 'no_join_key' || reason === 'ambiguous_source'
          ? `This is a gap between ${code} and the source list, not a finding about the property.`
          : `Nothing here is a statement about ${code}: FacilityWISE has not reached the source, so` +
            ' it does not know whether this site has codes on file — it has not looked. Report' +
            ' this to IT rather than to the property.'}
      </span>
    </div>
  );
}

/**
 * The sensitive half of the tab, for a caller who does not hold the permission.
 *
 * This is not a masked value: the API leaves the field out of the JSON, so
 * there is nothing on this page to uncover. The panel says so, because a reader
 * who assumes the value is in the payload goes looking in devtools — and
 * because "hidden" with no route to access is how someone decides the screen is
 * broken and phones the site for the code instead, which exposes it far more
 * widely than showing it here ever would.
 *
 * ⚠️ `reason` is why this is three sentences and not one. Being told to request
 * a permission you may already hold is its own wrong answer, so a WISE-IAM
 * outage says that it was an outage. The API sends the reason precisely so this
 * screen does not have to infer it from the absence of data.
 */
function SensitiveWithheld({
  what,
  permission,
  reason,
}: {
  what: string;
  permission: string | null;
  reason: SecurityDenialReason | undefined;
}) {
  return (
    <div className="unloaded">
      <b>{what} withheld</b>
      <span>
        {reason === 'authorization_unavailable'
          ? 'FacilityWISE could not reach WISE-IAM to check your permissions, so the security' +
            ' credentials were withheld. That is a denial by design, not an answer about your' +
            ' access — try again shortly, and report it to IT if it persists.'
          : reason === 'unauthenticated'
            ? 'This session is not signed in as far as the API is concerned, so the security' +
              ' credentials were withheld. Reload the page and sign in again.'
            : permission
              ? `Your account does not hold ${permission}, the permission that releases the` +
                ' security credentials. Ask IT to grant it in WISE-IAM.'
              : 'Your account does not hold the permission that releases the security' +
                ' credentials. Ask IT to grant it in WISE-IAM.'}{' '}
        The values are not sent to this browser at all, so there is nothing on this page to
        reveal.
      </span>
    </div>
  );
}

/**
 * A three-state boolean, as words.
 *
 * ⚠️ THIS EXISTS BECAUSE `dash(false)` IS A BLANK CELL. `dash()` passes
 * anything that is not `null`, `undefined`, `''` or `'None'` straight through,
 * so it hands React the literal `false` — and React renders a `false` child as
 * NOTHING. An empty cell on this tab means "not recorded", so a DVR whose
 * stored login is confirmed NOT an admin login would read exactly like one
 * whose column was never filled in. Both states are green in typecheck, build,
 * deploy and health; the only place the difference shows is the screen.
 *
 * `AdminAccess` is the only boolean in the whole security payload (verified
 * against the list's own metadata: `AdminAccess | Admin Access | Boolean`) and
 * it is three-state at the source — 112 true, 63 false, 83 rows that never say.
 * The API keeps that with `triBool`, so the last place the third state can be
 * destroyed is here.
 *
 * `null` goes through `dash()` rather than printing its own em-dash, so an
 * unrecorded boolean looks like every other unrecorded field on the page.
 */
function yesNo(v: boolean | null | undefined) {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return dash(null);
}

/** One inert cross-reference row. Matches the Business and IT tabs' link rows. */
const XREF_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  padding: '9px 15px',
  fontSize: 12.5,
};

/**
 * What the API sent for one section, or a stated fault if it sent nothing.
 *
 * `portal/src/lib/types.ts` is a HAND mirror of the API's types — there is no
 * shared package — so a section renamed on the API side arrives here as
 * `undefined` rather than as a type error, and `tsc` stays green on both sides
 * of the break. Reading a missing section as "no rows" would print "no gate
 * codes on file" for every facility in the portfolio, which is the one
 * conclusion this tab must never invite. So a missing section is a fault, and
 * it says so.
 */
function sectionState<T>(
  value: SecurityState<T> | undefined,
  list: string,
): SecurityState<T> {
  if (value && typeof value === 'object' && 'state' in value) return value;
  return {
    state: 'not_loaded',
    reason: 'read_failed',
    detail:
      `The API response carried no "${list}" section. That is a FacilityWISE contract fault ` +
      'between the API and this page, not a fact about the property.',
    // No key answered because no read happened. `'none'` is the honest value
    // and the type requires one — a fabricated state that claimed `'yardi'`
    // would make `matchFoot` stay silent for the right-looking reason.
    matchedOn: 'none',
  };
}

/**
 * Two footer sentences, or one, or none.
 *
 * `undefined` when nothing survives, so `Panel` leaves the footer bar out
 * entirely rather than drawing an empty rule under the table. The match
 * qualifier goes FIRST: it is about where the rows came from, which the reader
 * needs before deciding what to make of the columns that are missing.
 */
function foot(...parts: (string | undefined)[]): string | undefined {
  const kept = parts.filter((p): p is string => p !== undefined && p !== '');
  return kept.length > 0 ? kept.join(' ') : undefined;
}

/**
 * The quiet qualifier under a section whose rows were attributed by StoreID.
 *
 * ⚠️ DELIBERATELY NOT A BANNER, A COLOUR OR A WARNING ICON. `store` is a
 * legitimate answer, not a degraded one: it is the only key a row carrying a
 * retired Yardi code has, and the only key a facility whose code is shared
 * with another site may use at all. Shouting would make ordinary sections look
 * broken and teach people to ignore the loud thing.
 *
 * It is said at all because the StoreID is also the key that was WRONG on 13
 * rows on 2026-09-18 — two pairs of same-named facilities whose rows are
 * simply swapped, plus a handful of mistypings. The API now drops a row whose
 * code names somebody else, so what is left on this path is either unnamed or
 * unresolvable, and that is a weaker attribution than a row that says whose it
 * is. Someone about to drive to a gate is the person who finds out.
 *
 * `yardi` prints NOTHING. A section matched on the code the source's own
 * maintainers type needs no explanation, and annotating every panel with "this
 * one is fine" is how a reader learns to skip the annotation.
 *
 * ⚠️ ONE SENTENCE, AND IT HAS TO STAY ONE. This is per SECTION, and a
 * facility whose Yardi code is shared with another site — `brasta`, `lasson`,
 * the annex pairs, the four with no code at all — takes the StoreID path on
 * ALL SIX, so whatever is written here is printed six times on one screen.
 * A paragraph explaining the join would be five paragraphs, which is the
 * banner this was asked not to be. The reasoning lives in this comment, where
 * it is read once.
 */
function matchFoot(section: SecurityState<unknown>, storeId: number | null): string | undefined {
  if (section.state !== 'present' || section.matchedOn !== 'store') return undefined;
  // `storeId` cannot be null on this path — the API only reports `'store'`
  // after matching on one — but the facility type allows null, so the sentence
  // is written to survive it rather than printing "StoreID null".
  return (
    `Matched by StoreID${storeId === null ? '' : ` ${storeId}`} rather than by Yardi code, so the ` +
    'attribution is inferred rather than stated on the rows.'
  );
}

export function SecurityTab({ detail }: TabProps) {
  const { security, facility } = detail;

  // A contract guard, not defensive padding — same reason as `sectionState`
  // above. On a detail page an undefined here throws and blanks the screen, and
  // a blank screen is indistinguishable from "this facility has no security
  // data".
  if (!security) {
    return (
      <div className="unloaded">
        <b>Security data not returned</b>
        <span>
          The API response for this facility carries no security block, so there is nothing to
          render. That is a fault in FacilityWISE, not a fact about {facility.code} — report it to
          IT rather than treating this site as having no codes.
        </span>
      </div>
    );
  }

  const codes = sectionState(security.codes, 'codes');
  const gates = sectionState(security.gates, 'gates');
  const alarms = sectionState(security.alarms, 'alarms');
  const misc = sectionState(security.misc, 'misc');
  const dvr = sectionState(security.dvr, 'dvr');
  // `Facility IT Information`, Gate Access Control block only — ONE record per
  // site, so this is a `SecurityState<FacilityItInformation>` and renders as
  // key/value like `codes`, not as a table.
  const it = sectionState(security.it, 'it');

  // ⚠️ FAIL CLOSED, AND THAT MEANS `||`, NOT `??`. `access.withheld` is the
  // API's own answer and `redacted` is the same fact said a different way, so
  // either one saying "withheld" is enough. If NEITHER arrived — a renamed
  // field, an older API — the answer is "withheld", never "nothing was
  // withheld".
  //
  // ⚠️ `??` WAS NOT ENOUGH, BECAUSE IT ONLY FALLS THROUGH ON null/undefined.
  // When the two fields DISAGREE — `access.withheld === false` beside
  // `redacted === true` — `??` took the permissive one. That state is real
  // and instrumented, not hypothetical: it is exactly what a broken
  // `{ sensitive }` hand-off produces, and the API counts it as
  // `securityGuard.redactedForHolder` (routes/facilities.ts) precisely
  // because services/security-guard-alarm.ts calls it "invisible from the
  // outside". Rendering it permissively draws the guarded columns full of
  // em-dashes, i.e. "no gate note recorded" and "no DVR password recorded"
  // about values that exist and were withheld — a confident wrong answer in
  // the quiet direction, on the one tab whose contract is that the three
  // states stay three sentences. A granted caller is `false || false` and is
  // unchanged; a denied one is unchanged; only the contradiction moves, and
  // it moves closed.
  const withheld = (security.access?.withheld ?? true) || (security.redacted ?? true);
  const perm = security.access?.requiredPermission ?? null;
  const denial = security.access?.reason;
  const storeId = facility.storeId;

  /**
   * Shown under a table whose credential columns were left out of the payload.
   *
   * The column names here are the RENDERED headings, which is what the reader
   * just looked at. `security.withheld` carries the API's own field names for
   * the same set — same source, different vocabulary — and is what an audit
   * would read.
   *
   * ONE COLUMN PER ARGUMENT, not a pre-joined phrase, because the verb has to
   * agree with how many there are: the alarms panel withholds a single `Code`,
   * and a pre-joined string cannot be counted, so it printed "Code need the …
   * permission" to every user without the permission on every facility that
   * has an office alarm. There is no dev or test tier here to read that
   * sentence before a real user does.
   */
  const withheldFoot = (...columns: string[]) => {
    if (!withheld) return undefined;
    const plural = columns.length > 1;
    const names = plural
      ? `${columns.slice(0, -1).join(', ')} and ${columns[columns.length - 1]}`
      : columns[0];
    return (
      `${names} ${plural ? 'need' : 'needs'} the ${perm ?? 'security credentials'} permission ` +
      `and ${plural ? 'are' : 'is'} not sent to this browser.`
    );
  };

  /**
   * Which key attached each section's rows, computed once per section.
   *
   * Per SECTION and not once for the tab, because the six lists are joined
   * independently and genuinely disagree: a facility can match `Facility
   * Codes` on its Yardi code and fall back to its StoreID for the gates,
   * because the mis-keyed rows are scattered across five of the six lists.
   * A single tab-level qualifier would either over-claim or under-claim on
   * five panels out of six.
   */
  const codesMatch = matchFoot(codes, storeId);
  const gatesMatch = matchFoot(gates, storeId);
  const alarmsMatch = matchFoot(alarms, storeId);
  const miscMatch = matchFoot(misc, storeId);
  const dvrMatch = matchFoot(dvr, storeId);
  const itMatch = matchFoot(it, storeId);

  /** Lists that exist on the site and are knowingly unread. Usually one. */
  const notRead = security.notRead ?? [];

  /**
   * Everything we could not read, said ONCE.
   *
   * All six sections fail together whenever the failure is about the READ
   * rather than about a list: no managed identity, a revoked site grant, an
   * unreachable tenant. Six identical panels saying so would bury the one
   * sentence that matters, so a uniform failure collapses into one block and
   * the six data panels are not drawn at all. A section that fails ON ITS
   * OWN still gets its own panel — that is a different fact about a different
   * list.
   *
   * It is no longer the everyday state. Until 2026-09-18 every read returned
   * 403 because the per-site Graph grant had not been applied; it has been,
   * and the lists answer. This layout is now the exception it was written to
   * be.
   *
   * ⚠️ EVERY SECTION BELONGS IN THIS ARRAY, and a new one that is left out
   * fails SILENTLY IN THE QUIET DIRECTION. `every()` over a short array is
   * satisfied by the five it knows about, so if those five fail identically
   * while the sixth loads, the tab takes the collapsed layout and the sixth
   * panel — which has data — is never drawn. No error, no empty state, and
   * the hand-written type mirror cannot catch it either.
   */
  const sections = [codes, gates, alarms, misc, dvr, it];
  const uniformFailure =
    sections.every((s) => s.state === 'not_loaded') &&
    new Set(sections.map((s) => (s.state === 'not_loaded' ? s.reason : ''))).size === 1
      ? (sections[0] as Extract<SecurityState<unknown>, { state: 'not_loaded' }>)
      : null;

  /* The cross-reference and unread-list panels render in BOTH layouts. On the
     day this shipped the uniform-failure layout is what everyone sees, and a
     tab that is one paragraph long would read as "the rebuild lost the old
     cards" to anyone who used them. */
  const elsewhere = (
    <>
      <Panel
        title="Elsewhere in this record"
        footer={
          <>
            Each of these was on this tab as a partial copy — fewer columns, fewer rows,
            unlabelled as a subset. They are unchanged on the tabs that own them.
          </>
        }
      >
        <div className="links">
          <span style={{ ...XREF_ROW, borderBottom: '1px solid var(--border)' }}>
            <span className="lm">Features</span>
            Cameras, key pads, alarms, intercoms and emergency equipment
          </span>
          <span style={{ ...XREF_ROW, borderBottom: '1px solid var(--border)' }}>
            <span className="lm">Vendors</span>
            Security and life-safety vendors, with contacts, phone and COI
          </span>
          <span style={XREF_ROW}>
            <span className="lm">General</span>
            Office and gate access hours, all seven days
          </span>
        </div>
      </Panel>

      {/* A list this module knows about and deliberately does not read. Named
          so the tab never implies the six panels above are all there is.

          ⚠️ THIS IS NO LONGER `Facility IT Information`. That entry's reason —
          "bound on the Power App's IT screen, so its column names were never
          established" — expired on 2026-09-18 when all 61 were read from the
          list's own metadata, and it is now the "Gate access control" panel
          above. Leaving it here would have printed a false statement under a
          heading that says "not read from SharePoint". The API replaced it
          rather than emptying the array, because `[]` makes this panel vanish
          and the tab quietly starts implying the six lists it reads are all
          there is. Whatever occupies it is the API's answer, rendered as sent —
          this panel does not name a list of its own. */}
      {notRead.length > 0 && (
        <Panel title="Not read from SharePoint" count={notRead.length}>
          <div className="kv">
            {notRead.map((n) => (
              <div className="kvr" key={n.list}>
                <dt>{n.list}</dt>
                <dd>{n.reason}</dd>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </>
  );

  if (uniformFailure) {
    return (
      <div className="cols">
        <div className="col">
          <SecurityUnavailable
            what="The security lists"
            reason={uniformFailure.reason}
            detail={uniformFailure.detail}
            code={facility.code}
          />
        </div>
        <div className="col">{elsewhere}</div>
      </div>
    );
  }

  return (
    <div className="cols">
      <div className="col">
        {/* `Facility Codes` is a single record per site in the Power App — a
            form, not a gallery — so it renders as key/value rather than as a
            one-row table. All FIVE fields are sensitive, which is why this
            panel has no partial state: either the values are here or the panel
            explains where to get them. */}
        <Panel
          title="Codes"
          footer={
            <>
              {codesMatch ? `${codesMatch} ` : ''}
              These six panels read the same SharePoint lists the Facility Viewer v2 Power App
              reads — the FacilityDataCenter site, not the SQL mirror, which does not replicate
              them.
            </>
          }
        >
          {codes.state === 'not_loaded' ? (
            <SecurityUnavailable
              what="Codes"
              reason={codes.reason}
              detail={codes.detail}
              code={facility.code}
            />
          ) : codes.state === 'absent' ? (
            <NoSecurityRows list="codes record" code={facility.code} storeId={storeId} />
          ) : withheld ? (
            <SensitiveWithheld what="Codes" permission={perm} reason={denial} />
          ) : (
            <KeyValue
              rows={[
                [
                  'Labell exchange',
                  codes.data.labellExchange ? <Mono key="lx">{codes.data.labellExchange}</Mono> : null,
                ],
                ['Key box', codes.data.keyBox ? <Mono key="kb">{codes.data.keyBox}</Mono> : null],
                [
                  'Safe combination',
                  codes.data.safeCombination ? (
                    <Mono key="sc">{codes.data.safeCombination}</Mono>
                  ) : null,
                ],
                /* ⚠️ THE TWO ROWS THE POWER APP CANNOT SHOW. That app carries
                   both computer passwords in its security-edit AUDIT and binds
                   no control to either, so it can tell you the password changed
                   and not what it is. The columns are on `Facility Codes`
                   beside the safe combination (129 and 89 of 369 rows), and the
                   divergence is deliberate on the API side — see `FacilityCodes`
                   in lib/types.ts. Somebody comparing this panel against the old
                   screen is looking at an intended difference, not a leak. */
                [
                  'Computer 1 password',
                  codes.data.computer1Password ? (
                    <Mono key="c1">{codes.data.computer1Password}</Mono>
                  ) : null,
                ],
                [
                  'Computer 2 password',
                  codes.data.computer2Password ? (
                    <Mono key="c2">{codes.data.computer2Password}</Mono>
                  ) : null,
                ],
              ]}
            />
          )}
        </Panel>

        {/* The sensitive columns are rendered as a fragment guarded by
            `!withheld` rather than blanked per cell: a cell that prints an
            em-dash for a withheld code is indistinguishable from one that
            prints an em-dash for an empty column, and the two are different
            facts. The header goes with the cells for the same reason — a column
            heading over nothing is still a claim. That is how alarms, misc and
            DVR render; gates cannot, and the next comment says why. */}

        {/* ⚠️ GATES HAS NO NON-SENSITIVE COLUMN AT ALL, so it does not use the
            per-column guard the other three do. `Title` used to be its first
            column and it was the facility's Yardi code — the same string on
            every row of the page — so removing it leaves `User` and `Code`,
            both sensitive. Guarding those per column would render a table with
            a header row, N body rows and zero columns for a caller without the
            permission, which is not a rendering of anything. The whole table is
            therefore replaced by the withheld panel, and the COUNT stays in the
            panel header: "this site has N gate codes on file and you may not
            see them" is the honest answer, and the count is not a credential. */}
        <Panel
          title="Gate access codes"
          count={gates.state === 'present' ? gates.data.length : undefined}
          footer={gatesMatch}
        >
          {gates.state === 'not_loaded' ? (
            <SecurityUnavailable
              what="Gate access codes"
              reason={gates.reason}
              detail={gates.detail}
              code={facility.code}
            />
          ) : gates.state === 'absent' ? (
            <NoSecurityRows list="gate access codes" code={facility.code} storeId={storeId} />
          ) : withheld ? (
            <SensitiveWithheld what="Gate access codes" permission={perm} reason={denial} />
          ) : (
            <div className="tbl-x">
              <table className="mini-t">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Code</th>
                  </tr>
                </thead>
                <tbody>
                  {gates.data.map((g, i) => (
                    // Keyed on POSITION ALONE, which is the only thing left to
                    // key on: both of this row's fields are credentials, and a
                    // React key holding a gate code would be a copy of the
                    // value in a place no security review would think to look.
                    // Safe here because the list is rendered straight from the
                    // payload in source order and is never sorted, filtered or
                    // appended to on this screen.
                    <tr key={i}>
                      <td>{dash(g.user)}</td>
                      <td className="mono">{dash(g.code)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        {/* ⚠️ "GATE ACCESS CONTROL" IS THE LEGACY APP'S OWN HEADING, and it is
            deliberately next to "Gate access codes" despite how close the two
            titles read. They are different things and a reader has to be able
            to tell them apart: the panel above is the list of codes that open
            the gate, this one is the SYSTEM that runs it — the vendor's
            software, where it is hosted, how many keypads and cameras are
            installed. The Power App puts them a tab apart and calls this
            container "Gate Access Control" (`Src/Auxiliary Screen.pa.yaml:3318`);
            renaming it here would break the one word people already use for it.

            ⚠️ IT IS A PARTIAL READ OF A 61-COLUMN LIST. The API requests the
            ten columns of that container and nothing else, because the
            container's `Visible: =Or(MyTab.ID = 4, MyTab.ID = 6)` (:3298) makes
            it the only part of `Facility IT Information` the legacy Security
            tab shows. The list's ISP, firewall, VoIP and modem columns — some
            of them outright logins — are established by name on the API side
            and deliberately unselected. Do not "complete" this panel from that
            comment: a column printed here would first have to be read, and
            reading it publishes it to every devtools panel.

            ONE RECORD PER SITE (the app uses `LookUp`, not `Filter`), so this
            is key/value like Codes rather than a table. The two galleries the
            old screen draws — gate, then cameras — survive as the row ORDER,
            which is the same order they appear in on the legacy screen. */}
        <Panel
          title="Gate access control"
          footer={foot(
            itMatch,
            it.state === 'present' ? withheldFoot('Gate notes', 'Camera notes') : undefined,
          )}
        >
          {it.state === 'not_loaded' ? (
            <SecurityUnavailable
              what="Gate access control"
              reason={it.reason}
              detail={it.detail}
              code={facility.code}
            />
          ) : it.state === 'absent' ? (
            <NoSecurityRows
              list="gate access control record"
              code={facility.code}
              storeId={storeId}
            />
          ) : (
            /* Unlike Codes, this section is NOT wholly sensitive — eight of its
               ten columns are equipment facts that sit behind `facilities.view`,
               and a count is not a code. So it never collapses into
               `SensitiveWithheld`: the panel keeps rendering and the two notes
               ROWS are dropped, with the footer naming them. Dropping the row
               rather than printing an em-dash in it is the whole contract —
               "withheld" and "recorded but empty" are different sentences, and
               an em-dash would say the second about the first. */
            <KeyValue
              rows={[
                // The gate gallery, in its legacy order and with its legacy
                // labels (`Src/Auxiliary Screen.pa.yaml:3370`-:3382).
                ['Software', it.data.gateSoftware],
                ['Hosted location', it.data.gateSoftwareHostLocation],
                /* ⚠️ RENAMED FROM THE LEGACY "Notes" ON PURPOSE, and it is not
                   cosmetic. The old screen draws two galleries with their own
                   headings, so a bare "Notes" was unambiguous inside each; a
                   flat key/value list has no headings, so both rows would read
                   "Notes" and a reader could not tell the gate note from the
                   camera note. `KeyValue` also keys its rows on the LABEL, so
                   two rows called "Notes" would be a duplicate React key. */
                ...(withheld ? [] : ([['Gate notes', it.data.gateNotes]] as KVRow[])),
                ['# of keypads', it.data.gateKeypadQuantity],
                // The camera gallery, in its legacy order and labels (:3477-:3493).
                ['Total # of cameras', it.data.totalCameraQuantity],
                ['Number of DVRs/NVRs', it.data.videoRecordingDeviceQuantity],
                ['# of analog cameras', it.data.analogCameraQuantity],
                ['CCTV platform', it.data.cctvPlatform],
                ['# of IP cameras', it.data.ipCameraQuantity],
                ...(withheld ? [] : ([['Camera notes', it.data.cameraNotes]] as KVRow[])),
              ]}
            />
          )}
        </Panel>

        <Panel
          title="Office alarms"
          count={alarms.state === 'present' ? alarms.data.length : undefined}
          footer={foot(alarmsMatch, alarms.state === 'present' ? withheldFoot('Code') : undefined)}
        >
          {alarms.state === 'not_loaded' ? (
            <SecurityUnavailable
              what="Office alarms"
              reason={alarms.reason}
              detail={alarms.detail}
              code={facility.code}
            />
          ) : alarms.state === 'absent' ? (
            <NoSecurityRows list="office alarms" code={facility.code} storeId={storeId} />
          ) : (
            <div className="tbl-x">
              <table className="mini-t">
                {/* `Alarm type` IS the label here, and is all the label there
                    is now that `Title` has gone back to being a join key. It
                    was already the first column, so this table loses a column
                    rather than changing shape.

                    ⚠️ IT IS NOT A VOCABULARY. This comment used to read
                    "burglar, fire, septic". The column is Text, not Choice,
                    and its 337 populated cells hold 166 distinct values —
                    five of which are four digits and nothing else, i.e. the
                    alarm CODE typed into the type column, and on three of
                    those the guarded `Code` cell beside it is empty. Those
                    three print here, unguarded. It is left on this side of
                    the gate because it is this table's only non-credential
                    column and withholding it would collapse the panel; the
                    fix is in SharePoint. See the catalogue note in
                    api/src/services/security-repository.ts. */}
                <thead>
                  <tr>
                    <th>Alarm type</th>
                    {!withheld && <th>Code</th>}
                  </tr>
                </thead>
                <tbody>
                  {alarms.data.map((a, i) => (
                    <tr key={`${a.alarmType ?? ''}-${i}`}>
                      <td className="vcat">{dash(a.alarmType)}</td>
                      {!withheld && <td className="mono">{dash(a.code)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      <div className="col">
        <Panel
          title="Miscellaneous codes"
          count={misc.state === 'present' ? misc.data.length : undefined}
          footer={foot(
            miscMatch,
            misc.state === 'present' ? withheldFoot('Code', 'Password') : undefined,
          )}
        >
          {misc.state === 'not_loaded' ? (
            <SecurityUnavailable
              what="Miscellaneous codes"
              reason={misc.reason}
              detail={misc.detail}
              code={facility.code}
            />
          ) : misc.state === 'absent' ? (
            <NoSecurityRows list="miscellaneous codes" code={facility.code} storeId={storeId} />
          ) : (
            <div className="tbl-x">
              <table className="mini-t">
                {/* `Item` — what the code opens — is the label, and is the only
                    one this list has now that `Title` is understood to be the
                    Yardi code. It is populated on 99.2% of the rows, so the
                    table reads as a list of things rather than as a column of
                    em-dashes; where it is empty the em-dash is the truth. */}
                <thead>
                  <tr>
                    <th>Item</th>
                    {!withheld && (
                      <>
                        <th>Code</th>
                        <th>Password</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {misc.data.map((m, i) => (
                    <tr key={`${m.item ?? ''}-${i}`}>
                      <td>{dash(m.item)}</td>
                      {!withheld && (
                        <>
                          <td className="mono">{dash(m.code)}</td>
                          <td className="mono">{dash(m.password)}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        {/* The web address is rendered as text, never as a link, and as of
            2026-09-18 it is rendered only BEHIND THE PERMISSION. These are
            in-store recorders, so the value is as often a bare host or an
            address:port as it is a URL — an href built from one would be a
            relative link off this portal, which reads as a broken screen — and
            a navigation would put the address in browser history and in a
            referrer, two more copies of something that reaches a camera system
            from outside the property. On 64 of the 112 populated rows it is a
            public IP and on 60 it carries an explicit port, which is why it
            sits beside the login rather than beside make and model. */}
        <Panel
          title="DVR"
          count={dvr.state === 'present' ? dvr.data.length : undefined}
          /* All seven withheld columns are named, not just the login pair,
             and IN THE ORDER THE TABLE WOULD HAVE SHOWN THEM. The footer is
             what tells a reader WHICH columns are missing from the table they
             just looked at, and a list that stopped at "User name and
             Password" would leave four more absent columns unexplained —
             which reads as a narrower table rather than a redacted one.
             `Web address` is on this list because it moved behind the
             permission on 2026-09-18; without it the table silently loses its
             third column with nothing to say why. */
          footer={foot(
            dvrMatch,
            dvr.state === 'present'
              ? withheldFoot(
                  'Web address',
                  'User name',
                  'Password',
                  'Admin access',
                  'Port forwarding',
                  'Own static IP',
                  'Notes',
                )
              : undefined,
          )}
        >
          {dvr.state === 'not_loaded' ? (
            <SecurityUnavailable
              what="DVR"
              reason={dvr.reason}
              detail={dvr.detail}
              code={facility.code}
            />
          ) : dvr.state === 'absent' ? (
            <NoSecurityRows list="DVR records" code={facility.code} storeId={storeId} />
          ) : (
            <div className="tbl-x">
              <table className="mini-t">
                {/* ⚠️ `Make` AND `Model` ARE WHY THIS TABLE STILL HAS A HEADING.
                    The bold first cell used to be `Title`, which this tab
                    called the make and model and which was actually the Yardi
                    code — the same string on all of a site's rows. Dropping it
                    would have left a DVR row identified by nothing but its web
                    address. `Make` and `Model` are real columns on `Facility
                    DVR Information`, newly read; they are not credentials, so
                    they stay behind `facilities.view` and stay on screen for
                    a caller who may not see the login — and since the web
                    address moved behind the permission they are the ONLY two
                    columns such a caller gets, which is what keeps this a
                    table of named rows rather than the everything-withheld
                    shape the gates panel has. They are separate columns
                    rather than one joined cell
                    because they are populated independently — 151 and 127 of
                    258 rows — and joining them would print a stray separator
                    wherever only one is recorded. */}
                {/* ⚠️ THE HEADERS AND THE CELLS ARE GUARDED BY THE SAME
                    EXPRESSION, in the same order, and adding a column means
                    editing both fragments. A `<th>` without its `<td>` shifts
                    every value one column left for one class of user and not
                    the other, which is a table that reads correctly to whoever
                    tests it and wrongly to everybody else — here that would
                    print a port number under "Own static IP". */}
                <thead>
                  <tr>
                    <th>Make</th>
                    <th>Model</th>
                    {!withheld && (
                      <>
                        <th>Web address</th>
                        <th>User name</th>
                        <th>Password</th>
                        <th>Admin access</th>
                        <th>Port forwarding</th>
                        <th>Own static IP</th>
                        <th>Notes</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {dvr.data.map((d, i) => (
                    // Make and model are not credentials, so they are safe in
                    // a key. ⚠️ THE WEB ADDRESS IS NOT IN THE KEY AND MUST NOT
                    // BE PUT BACK: it is withheld now, so for most callers it
                    // is `undefined` and every row would key on the same
                    // string — and for a caller who does hold the permission
                    // it would write a reach-through address into a React key.
                    // Position disambiguates the sites that run several
                    // identical recorders.
                    <tr key={`${d.make ?? ''}-${d.model ?? ''}-${i}`}>
                      <td>
                        <b>{dash(d.make)}</b>
                      </td>
                      <td>{dash(d.model)}</td>
                      {!withheld && (
                        <>
                          <td className="mono">{dash(d.webAddress)}</td>
                          <td className="mono">{dash(d.userName)}</td>
                          <td className="mono">{dash(d.password)}</td>
                          {/* ⚠️ `yesNo`, NEVER `dash`. This column is a real
                              boolean and `dash(false)` hands React a literal
                              `false`, which renders as an empty cell — i.e.
                              "not recorded", which is the opposite of what a
                              recorded `false` says. 63 of the 258 rows are
                              false and 83 genuinely do not say. */}
                          <td>{yesNo(d.adminAccess)}</td>
                          <td className="mono">{dash(d.portForwarding)}</td>
                          <td className="mono">{dash(d.ownStaticIp)}</td>
                          {/* Prose, so no `mono` — and the only field on this
                              row the legacy app never showed on any screen. */}
                          <td>{dash(d.notes)}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        {/* Three things left this tab, and each one is named here with the tab
            that owns it IN FULL. They are labels, not links — the tab strip is
            two rows up and a second way to move between tabs would be a control
            competing with it — but saying nothing would make a rebuild look
            like data loss to the people who used the old cards. */}
        {elsewhere}
      </div>
    </div>
  );
}

// ─── Vendors ────────────────────────────────────────────────────────────────

export function VendorsTab({ detail }: TabProps) {
  if (!detail.vendors.present) return <NotLoaded what="vendor" />;
  const V = detail.vendors.data;

  return (
    <Panel
      title={`Service vendors — ${V.length}`}
      footer={
        <>
          <span className="lm">ContractWISE</span>
          Contracts governing these vendors are managed in ContractWISE. Expired certificates are
          flagged in amber.
        </>
      }
    >
      <div className="tbl-x">
        <table className="mini-t vend">
          <thead>
            <tr>
              <th>Service</th>
              <th>Vendor</th>
              <th>Contact</th>
              <th>Phone</th>
              <th>Preference</th>
              <th>COI expires</th>
            </tr>
          </thead>
          <tbody>
            {V.map((v, i) => (
              <tr key={`${v.s}-${v.v}-${i}`}>
                <td className="vcat">{v.s}</td>
                <td>
                  <b>{v.v}</b>
                  {v.type && <div className="vsub">{v.type}</div>}
                </td>
                {/* Service_Vendors carries four contact slots. The primary one
                    is shown inline; the rest are named underneath rather than
                    dropped, because "who else can I call" is the actual
                    question someone opens this tab with. Vendor contacts are
                    external, so they get a mailto and a tel — never a Teams
                    link, which would never resolve for them. */}
                <td>
                  {v.c || v.email ? (
                    <>
                      {v.email ? <a href={`mailto:${v.email}`}>{v.c ?? v.email}</a> : v.c}
                      {v.contacts.length > 1 && (
                        <div className="vsub">
                          +{v.contacts.length - 1} more:{' '}
                          {v.contacts
                            .slice(1)
                            .map((k) => k.name ?? k.phone ?? k.email)
                            .filter(Boolean)
                            .join(', ')}
                        </div>
                      )}
                    </>
                  ) : (
                    dash(null)
                  )}
                </td>
                <td className="mono">
                  {v.ph ? <a href={`tel:${v.ph.replace(/[^\d+]/g, '')}`}>{v.ph}</a> : '—'}
                </td>
                <td>
                  {v.p ? <span className={`chip ${v.p === 'Primary' ? 'ok' : 'q'} sm`}>{v.p}</span> : dash(null)}
                </td>
                <td>
                  <CoiCell ins={v.ins} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

// ─── Documents ──────────────────────────────────────────────────────────────

export function DocumentsTab({ detail }: TabProps) {
  const x = detail.facility;
  const { links, generatedAt } = detail.documents;
  const asOf = new Date(generatedAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <Panel
      title="Documents"
      footer={
        <>
          Site addresses come from a verified map of the SharePoint tenant, taken {asOf} — not
          derived from the facility name. Only 280 of 328 team-site addresses can be reproduced
          from a title, so a derived link would 404 for roughly one property in seven.
        </>
      }
    >
      <div className="links">
        {links.map((l) =>
          l.url ? (
            <a className="doclink" key={l.key} href={l.url} target="_blank" rel="noreferrer">
              <span className="dl-i" aria-hidden="true">
                {l.key === 'services' ? '◈' : '▤'}
              </span>
              <span className="dl-b">
                <b>
                  {l.label} <span className="dl-x">↗</span>
                </b>
                <span>{l.detail}</span>
              </span>
            </a>
          ) : (
            /* Absent, and it says WHY. A greyed row with no reason reads as a
               bug in this screen rather than as a fact about the property. */
            <span className="doclink off" key={l.key}>
              <span className="dl-i" aria-hidden="true">
                ▤
              </span>
              <span className="dl-b">
                <b>{l.label}</b>
                <span>{l.absentReason ?? l.detail}</span>
              </span>
            </span>
          ),
        )}
      </div>
      {x.docSiteCreated === false && (
        <div className="pnl-foot">
          The facility master records that no document site has been created for {x.code}.
        </div>
      )}
    </Panel>
  );
}

export const TAB_BODIES: Record<TabName, (p: TabProps) => JSX.Element> = {
  General: GeneralTab,
  Responsibilities: ResponsibilitiesTab,
  Business: BusinessTab,
  Operations: OperationsTab,
  Features: FeaturesTab,
  IT: ItTab,
  Security: SecurityTab,
  Vendors: VendorsTab,
  Documents: DocumentsTab,
};
